import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { trackLandingPage } from '../../../tracking';
import {
  FAA_LANDING_PAGE_MODULE,
  getVisibleLandingPageModules,
  trackSelectSearchSuggestion,
} from '../../../tracking/landing-page';
import type { FaaLandingPageModule } from '../../../tracking/landing-page';
import type { Option } from '../../search-autocomplete/types';

interface UseLandingPageTrackingOptions {
  mixpanelHasInitialized: boolean;
  showCallToActions: boolean;
  showUltimateGuide: boolean;
  showSuburbSnapshot: boolean;
}

interface UseLandingPageTrackingResult {
  onModuleRendered: (module: FaaLandingPageModule) => void;
  onSuggestionSelect: (params: {
    item: Option;
    position: number;
    textTyped: string;
    totalSuggestions?: number;
  }) => void;
  onSearchInputInteraction: () => void;
}

const useLandingPageTracking = ({
  mixpanelHasInitialized,
  showCallToActions,
  showUltimateGuide,
  showSuburbSnapshot,
}: UseLandingPageTrackingOptions): UseLandingPageTrackingResult => {
  const hasTrackedView = useRef(false);
  const hasTrackedSearchInputInteraction = useRef(false);
  const hasPendingSearchInputInteraction = useRef(false);
  const pendingSuggestionSelect = useRef<
    Parameters<typeof trackSelectSearchSuggestion>[0] | null
  >(null);
  const [loadedModules, setLoadedModules] = useState<FaaLandingPageModule[]>(
    [],
  );

  const visibleModules = useMemo(
    () =>
      getVisibleLandingPageModules({
        hasContentCarouselGuides: showUltimateGuide,
        hasCtaDriverGetAppraisal: showCallToActions,
        hasCtaDriverTrackHome: showCallToActions,
        hasCtaDriverRecentlySold: showCallToActions,
        hasSuburbSnapshot: showSuburbSnapshot,
      }),
    [showUltimateGuide, showCallToActions, showSuburbSnapshot],
  );

  const onModuleRendered = useCallback((module: FaaLandingPageModule): void => {
    setLoadedModules((previousModules) => {
      if (previousModules.includes(module)) {
        return previousModules;
      }

      return [...previousModules, module];
    });
  }, []);

  useEffect(() => {
    if (showSuburbSnapshot) {
      onModuleRendered(FAA_LANDING_PAGE_MODULE.SUBURB_SNAPSHOT);
    }
  }, [showSuburbSnapshot, onModuleRendered]);

  useEffect(() => {
    const allVisibleModulesLoaded = visibleModules.every((module) =>
      loadedModules.includes(module),
    );

    if (
      mixpanelHasInitialized &&
      allVisibleModulesLoaded &&
      !hasTrackedView.current
    ) {
      hasTrackedView.current = true;
      trackLandingPage.trackViewLandingPage(visibleModules);
    }
  }, [mixpanelHasInitialized, visibleModules, loadedModules]);

  const onSearchInputInteraction = useCallback((): void => {
    if (hasTrackedSearchInputInteraction.current) {
      return;
    }

    if (mixpanelHasInitialized) {
      hasTrackedSearchInputInteraction.current = true;
      trackLandingPage.trackSearchInputInteraction();
    } else {
      hasPendingSearchInputInteraction.current = true;
    }
  }, [mixpanelHasInitialized]);

  useEffect(() => {
    if (
      mixpanelHasInitialized &&
      hasPendingSearchInputInteraction.current &&
      !hasTrackedSearchInputInteraction.current
    ) {
      hasPendingSearchInputInteraction.current = false;
      hasTrackedSearchInputInteraction.current = true;
      trackLandingPage.trackSearchInputInteraction();
    }
  }, [mixpanelHasInitialized]);

  const onSuggestionSelect = useCallback<
    UseLandingPageTrackingResult['onSuggestionSelect']
  >(
    (params) => {
      if (mixpanelHasInitialized) {
        trackSelectSearchSuggestion(params);
      } else {
        pendingSuggestionSelect.current = params;
      }
    },
    [mixpanelHasInitialized],
  );

  useEffect(() => {
    if (mixpanelHasInitialized && pendingSuggestionSelect.current) {
      trackSelectSearchSuggestion(pendingSuggestionSelect.current);
      pendingSuggestionSelect.current = null;
    }
  }, [mixpanelHasInitialized]);

  return {
    onModuleRendered,
    onSuggestionSelect,
    onSearchInputInteraction,
  };
};

export default useLandingPageTracking;
